// Partner cockpit — inbox-ticket drill-in (B5B-PARTNER-COCKPIT §3). One ticket's full context rendered
// inside the cockpit chrome: meta + linked processing + its state-transition history. The mandate is
// re-checked server-side (403 if the ticket's tenant is out of scope). Read-only. Behind real auth.
import { useParams, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Inbox } from "lucide-react";
import { cockpit, CockpitError } from "../lib/cockpit";
import { humanize } from "../lib/format";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

function fmt(d: string | null): string { return d ? new Date(d).toLocaleString() : "—"; }

export function TicketDetailRoute() {
  const { ticketId } = useParams({ from: "/ticket/$ticketId" });
  const { data, isLoading, error } = useQuery({ queryKey: ["cockpit-ticket", ticketId], queryFn: () => cockpit.ticket(ticketId), retry: false });

  if (error instanceof CockpitError && (error.status === 403 || error.status === 404)) {
    return <div className="flex flex-col gap-4"><PageHeader title="Ticket" /><Card><CardContent className="py-8 text-center text-sm text-destructive">This ticket is outside your mandate.</CardContent></Card></div>;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <Button asChild size="sm" variant="ghost"><Link to="/inbox"><ArrowLeft className="size-4" /> Inbox</Link></Button>
      </div>
      {isLoading && <Skeleton className="h-24" />}
      {error && !(error instanceof CockpitError && (error.status === 403 || error.status === 404)) && (
        <Card><CardContent className="py-8 text-center text-sm text-destructive">{(error as Error).message}</CardContent></Card>
      )}
      {data && (
        <>
          <PageHeader title={data.title} description={data.tenantName} />
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary">{humanize(data.state ?? data.lifecycle)}</Badge>
            {data.priority && <Badge variant="outline">priority {data.priority}</Badge>}
            <Badge variant="outline">{data.tenantName}</Badge>
            {data.resolvedAt && <Badge variant="outline" className="text-emerald-700">resolved</Badge>}
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader><CardTitle className="text-base">Details</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between gap-3"><span className="text-muted-foreground">Lifecycle</span><span>{humanize(data.lifecycle)}</span></div>
                <div className="flex justify-between gap-3"><span className="text-muted-foreground">Due</span><span>{fmt(data.dueAt)}</span></div>
                <div className="flex justify-between gap-3"><span className="text-muted-foreground">Resolved</span><span>{fmt(data.resolvedAt)}</span></div>
                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground">Processing</span>
                  {data.verwerkingId
                    ? <Link to="/verwerking/$verwerkingId" params={{ verwerkingId: data.verwerkingId }} className="truncate text-primary hover:underline">{data.verwerkingName ?? "View"}</Link>
                    : <span>—</span>}
                </div>
                {data.body && <p className="whitespace-pre-line border-t pt-2 text-muted-foreground">{data.body}</p>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Inbox className="size-4" />History</CardTitle></CardHeader>
              <CardContent>
                {data.transitions.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No state transitions recorded.</p>
                ) : (
                  <ul className="m-0 list-none space-y-2 p-0 text-sm">
                    {data.transitions.map((tr) => (
                      <li key={tr.id} className="flex items-center justify-between gap-3 border-b py-1.5 last:border-b-0">
                        <span className="min-w-0 truncate">{tr.fromState ? `${humanize(tr.fromState)} → ` : ""}<span className="font-medium">{humanize(tr.toState)}</span>{tr.transitionKind ? <span className="text-muted-foreground"> · {humanize(tr.transitionKind)}</span> : null}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">{fmt(tr.transitionedAt)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
